import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SortingState } from "@tanstack/react-table";
import useUserStore from "@/stores/user.store";
import useUserTokenStore from "@/stores/user-token.store";
import { parseTenantIdFromAccessToken } from "@/utils/auth";
import {
  DEFAULT_WORKSPACE_SORTING,
  persistWorkspaceSorting,
  readWorkspaceSorting,
  WorkspaceSortScope,
} from "@/lib/workspaceSortPreference";

/**
 * `useState` for the workspace list's sort, except the choice outlives the page
 * (LAR-132).
 *
 * Both halves of the scope come from stores the app hydrates synchronously — the
 * persisted user and access token — so the saved sort is in place on the very
 * first render and the list never fetches the default sort first.
 *
 * Clearing the sort ("Fjern sortering") goes back to the default rather than to
 * no sort at all (LAR-570).
 */
export const usePersistedWorkspaceSorting = (): [
  SortingState,
  React.Dispatch<React.SetStateAction<SortingState>>,
] => {
  const userId = useUserStore((state) => state.me?.id ?? null);
  const token = useUserTokenStore((state) => state.token);
  const tenantId = useMemo(() => parseTenantIdFromAccessToken(token), [token]);

  // Memoised on the ids themselves so a token refresh that re-issues the same
  // tenant is not mistaken for a tenant switch below.
  const scope = useMemo<WorkspaceSortScope | null>(
    () => (userId && tenantId ? { userId, tenantId } : null),
    [userId, tenantId]
  );

  const [sorting, setSorting] = useState<SortingState>(() =>
    readWorkspaceSorting(scope)
  );
  const syncedScope = useRef(scope);

  useEffect(() => {
    // Restoring and saving share one effect on purpose: a separate restore
    // effect would leave this one holding the previous scope's `sorting` on the
    // render that adopts a new scope, and it would save that over the new
    // scope's own sort before React re-rendered.
    if (syncedScope.current !== scope) {
      syncedScope.current = scope;
      setSorting(readWorkspaceSorting(scope));
      return;
    }

    persistWorkspaceSorting(scope, sorting);
  }, [scope, sorting]);

  // An empty sort leaves the list in the API's own order with no column marked,
  // and only a reload read the default back. Mapping it here shows the default
  // at once; it is still stored as no entry, since it is the default. The
  // updater form is resolved inside `setSorting` so it sees the latest state.
  const setSortingOrDefault = useCallback<
    React.Dispatch<React.SetStateAction<SortingState>>
  >((action) => {
    setSorting((current) => {
      const next = typeof action === "function" ? action(current) : action;
      return next.length > 0 ? next : DEFAULT_WORKSPACE_SORTING;
    });
  }, []);

  return [sorting, setSortingOrDefault];
};
